import * as React from 'react';

import cn from 'classnames';
import DatePicker from 'react-datepicker';
import tinytime from 'tinytime';

import { Input, Select, Text } from '@components/common';
import { LocationInput } from '@components/common/LocationInput';
import { OperatorSelect } from '@components/GQSurveyBuilder/components/SkipLogicSlideOut/OperatorSelect';
import { ValueSelect } from '@components/GQSurveyBuilder/components/SkipLogicSlideOut/ValueSelect';

import { answerValueIsArray, Rule } from '../utils';

interface RuleLogicProps {
  ruleId: string;
  question: ScreenerField;
  ruleLogic: Rule['logic'];
  onUpdateRuleLogic: (operator: Rule['logic']['operator'], value: Rule['logic']['answer_value']) => void;
  allRules?: Rule[];
  currentRuleIndex?: number;
}
const dateTemplate = tinytime('{MM} {DD}, {YYYY}');

const hasOptions = (fieldType: string) => ['yes_no', 'multiple_choice', 'single_choice'].includes(fieldType);
const isText = (fieldType: string) => ['long_text', 'short_text', 'email', 'website'].includes(fieldType);
const isNumber = (fieldType: string) => ['number', 'number_range'].includes(fieldType);
const isDate = (fieldType: string) => fieldType === 'date';
const isLocation = (fieldType: string) => fieldType === 'location';

export const RuleLogic: React.FC<RuleLogicProps> = ({
  ruleId,
  question,
  ruleLogic,
  onUpdateRuleLogic,
  allRules = [],
  currentRuleIndex = 0
}) => {
  const { operator, answer_value: answerValue } = ruleLogic;
  const shouldHideInputAnswer = ['any_answer', 'no_answer'].includes(ruleLogic.operator);

  const handleInputChange = (value: string) => {
    onUpdateRuleLogic(operator, value);
  };

  return (
    <div>
      <div className='mb-2'>
        <div className='inline-block'>
          <OperatorSelect
            key={`${ruleId}_operator`}
            question={question}
            value={operator}
            onChange={(newOperator) => onUpdateRuleLogic(newOperator, '')}
            allRules={allRules}
            currentRuleIndex={currentRuleIndex}
          />
        </div>
      </div>
      {!shouldHideInputAnswer && operator && (
        <div>
          {hasOptions(question.field_type) &&
            (() => {
              const options = question.field_type === 'yes_no' ? ['Yes', 'No'] : [...(question.options || [])];
              const isMultipleChoice = question.field_type === 'multiple_choice';
              const isSingleChoice = question.field_type === 'single_choice';

              if (question.other && (isMultipleChoice || isSingleChoice)) {
                options?.push('Other');
              }

              if (question.none_of_above && isMultipleChoice) {
                options?.push('None of the above');
              }

              const isValueAnArray = answerValueIsArray(question.field_type, operator);
              const isOptionDisabled = false; // todo

              if (!isValueAnArray) {
                return (
                  <Select
                    value={(answerValue as string) || ''}
                    options={options.map((option) => ({
                      label: option,
                      value: option,
                      disabled: isOptionDisabled
                    }))}
                    onChange={(selectedAnswerValueOption) => {
                      onUpdateRuleLogic(operator, selectedAnswerValueOption);
                    }}
                    renderLabel={({ label, disabled }) => (
                      <div className={cn({ 'text-gray-500': disabled })}>
                        <Text h='400'>{label}</Text>
                      </div>
                    )}
                    className='bg-white'
                    placeholder='Select option...'
                  />
                );
              }

              return (
                <ValueSelect
                  options={options.map((option) => ({ label: option, value: option }))}
                  value={Array.isArray(answerValue) ? answerValue : []}
                  onChange={(vals) => onUpdateRuleLogic(operator, vals)}
                  multi
                  placeholder='Select option...'
                  disabled={false}
                />
              );
            })()}
          {isText(question.field_type) && (
            <Input
              name='value'
              placeholder='Enter words'
              className='tablet:text-sm my-2 block w-full rounded-sm border border-gray-300 p-2 focus:border-indigo-500 focus:ring-indigo-500'
              value={answerValue as string}
              onChange={handleInputChange}
            />
          )}
          {isNumber(question.field_type) && (
            <div className='flex-1'>
              <Input
                name='value'
                placeholder='Enter number'
                type='number'
                className='tablet:text-sm my-2 block w-full rounded-sm border border-gray-300 p-2 focus:border-indigo-500 focus:ring-indigo-500'
                value={answerValue as string}
                onChange={handleInputChange}
              />
            </div>
          )}
          {isDate(question.field_type) && (
            <DatePicker
              onChange={(v) => onUpdateRuleLogic(operator, v ? v.getTime().toString() : '')}
              wrapperClassName='w-full'
              className='tablet:text-sm my-2 block w-full rounded-sm border border-gray-300 p-2 focus:border-indigo-500 focus:ring-indigo-500'
              placeholderText='Enter date'
              name='date'
              value={answerValue ? dateTemplate.render(new Date(Number(answerValue))) : null}
              popperPlacement='bottom-start'
              customInput={<input />}
              showYearDropdown
            />
          )}
          {isLocation(question.field_type) && (
            <LocationInput
              id={String(question.id)}
              placeholder='Enter location'
              requestTypes={['(regions)']}
              inputValue={answerValue as string}
              setValue={(value) => onUpdateRuleLogic(operator, value as string)}
            />
          )}
        </div>
      )}
    </div>
  );
};
